import React, { useState, useMemo, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  BiLoaderAlt,
  BiSearch,
  BiChevronDown,
  BiChevronRight,
  BiMemoryCard,
  BiBrain,
  BiPackage,
  BiLineChart,
  BiWorld,
  BiTargetLock,
  BiStopwatch,
} from 'react-icons/bi'
import {
  FiTruck,
  FiShield,
  FiBarChart2,
  FiMail,
  FiDownload,
  FiDatabase,
  FiCheckCircle,
  FiSettings,
  FiTrendingUp,
  FiUsers,
  FiShoppingCart,
  FiTarget,
  FiDollarSign,
  FiCalendar,
  FiCloud,
  FiFile,
  FiTerminal,
  FiCode,
  FiXCircle,
  FiBriefcase,
  FiCompass,
  FiUpload,
} from 'react-icons/fi'
import { stripLeadingPlanBlock } from '../utils/planBlock'
import { formatCalendarDate } from '../../../modules/calendarDate'
import {
  cleanPlanItem,
  extractPlanItems,
  isPlanText,
} from '../utils/agentPlanSteps'
import AgentResponseRenderer from './AgentResponseRenderer'

interface AgentStep {
  iteration: number
  status: 'running' | 'completed' | 'failed'
  thought?: string
  tool?: string
  toolInput?: any
  toolOutput?: any
  toolUseId?: string
  insight?: string
  timestamp?: number
}

interface MemoryUpdate {
  type: string
  message: string
  savedChars: number
  currentSize: number
  timestamp: number
}

interface Narration {
  text: string
  atStep: number
  workedForMs: number
  timestamp: number
}

import { type CreditSummary, formatCredits } from '../../../sharedComponents/AIChatBox/MessageCreditCost'

interface AgentChainOfThoughtProps {
  steps: AgentStep[]
  status: 'idle' | 'running' | 'completed' | 'failed' | 'cancelled'
  finalResponse?: string
  memoryUpdate?: MemoryUpdate | null
  narrations?: Narration[]
  messageId?: string
  /** #935: what this prompt cost, model vs tools. Rendered in the answer footer. */
  creditSummary?: CreditSummary | null
}

// Tool metadata for icons, labels, and tooltips
const toolMetadata: Record<string, {
  icon: React.ComponentType<any>
  label: string
  color: string
  bgColor: string
  category: string
  tooltip?: string
}> = {
  search: {
    icon: BiSearch,
    label: 'Search',
    color: 'tw-text-blue-600',
    bgColor: 'tw-bg-blue-50',
    category: 'Query',
  },
  fetch: {
    icon: FiDatabase,
    label: 'Fetch',
    color: 'tw-text-indigo-600',
    bgColor: 'tw-bg-indigo-50',
    category: 'Query',
  },
  insure_shipment: {
    icon: FiShield,
    label: 'Insure Shipment',
    color: 'tw-text-emerald-600',
    bgColor: 'tw-bg-emerald-50',
    category: 'Shipping',
  },
  void_insurance: {
    icon: FiShield,
    label: 'Void Insurance',
    color: 'tw-text-red-600',
    bgColor: 'tw-bg-red-50',
    category: 'Shipping',
  },
  update_shipping_address: {
    icon: FiTruck,
    label: 'Update Address',
    color: 'tw-text-orange-600',
    bgColor: 'tw-bg-orange-50',
    category: 'Shipping',
  },
  update_auto_insurance_settings: {
    icon: FiSettings,
    label: 'Auto Insurance',
    color: 'tw-text-purple-600',
    bgColor: 'tw-bg-purple-50',
    category: 'Settings',
  },
  update_carrier_first_hundred_coverage: {
    icon: FiShield,
    label: 'Carrier Coverage',
    color: 'tw-text-teal-600',
    bgColor: 'tw-bg-teal-50',
    category: 'Settings',
  },
  update_carrier_insurance_rule: {
    icon: FiSettings,
    label: 'Insurance Rules',
    color: 'tw-text-violet-600',
    bgColor: 'tw-bg-violet-50',
    category: 'Settings',
  },
  get_current_shipping_settings: {
    icon: FiTruck,
    label: 'Shipping Settings',
    color: 'tw-text-slate-600',
    bgColor: 'tw-bg-slate-50',
    category: 'Settings',
  },
  export_to_email: {
    icon: FiMail,
    label: 'Email Export',
    color: 'tw-text-pink-600',
    bgColor: 'tw-bg-pink-50',
    category: 'Export',
  },
  export_to_google_drive: {
    icon: FiCloud,
    label: 'Google Drive',
    color: 'tw-text-green-600',
    bgColor: 'tw-bg-green-50',
    category: 'Export',
  },
  import_csv: {
    icon: FiUpload,
    label: 'Data Import',
    color: 'tw-text-emerald-600',
    bgColor: 'tw-bg-emerald-50',
    category: 'Import',
  },
  export_to_csv: {
    icon: FiDownload,
    label: 'CSV Download',
    color: 'tw-text-gray-600',
    bgColor: 'tw-bg-gray-100',
    category: 'Export',
  },
  get_traffic_data: {
    icon: FiBarChart2,
    label: 'Traffic Data',
    color: 'tw-text-cyan-600',
    bgColor: 'tw-bg-cyan-50',
    category: 'Analytics',
  },
  get_traffic_metrics: {
    icon: FiTrendingUp,
    label: 'Traffic Metrics',
    color: 'tw-text-blue-600',
    bgColor: 'tw-bg-blue-50',
    category: 'Analytics',
  },
  get_ads_performance: {
    icon: FiTarget,
    label: 'Ads Performance',
    color: 'tw-text-amber-600',
    bgColor: 'tw-bg-amber-50',
    category: 'Analytics',
  },
  get_traffic_summary: {
    icon: BiLineChart,
    label: 'Traffic Summary',
    color: 'tw-text-sky-600',
    bgColor: 'tw-bg-sky-50',
    category: 'Analytics',
  },
  modify_google_ads: {
    icon: FiTarget,
    label: 'Google Ads',
    color: 'tw-text-amber-600',
    bgColor: 'tw-bg-amber-50',
    category: 'Action',
  },
  modify_facebook_ads: {
    icon: FiTarget,
    label: 'Facebook Ads',
    color: 'tw-text-blue-600',
    bgColor: 'tw-bg-blue-50',
    category: 'Action',
  },
  search_google_ads: {
    icon: BiSearch,
    label: 'Google Ads Search',
    color: 'tw-text-amber-600',
    bgColor: 'tw-bg-amber-50',
    category: 'Query',
  },
  google_web_search: {
    icon: BiSearch,
    label: 'Google Search',
    color: 'tw-text-sky-600',
    bgColor: 'tw-bg-sky-50',
    category: 'Query',
  },
  search_facebook_ads: {
    icon: BiSearch,
    label: 'Facebook Ads Search',
    color: 'tw-text-blue-600',
    bgColor: 'tw-bg-blue-50',
    category: 'Query',
  },
  get_google_ads_accounts: {
    icon: FiUsers,
    label: 'Google Ads Accounts',
    color: 'tw-text-amber-600',
    bgColor: 'tw-bg-amber-50',
    category: 'Query',
  },
  list_google_ads_client_accounts: {
    icon: FiUsers,
    label: 'Google Ads MCC Clients',
    color: 'tw-text-amber-600',
    bgColor: 'tw-bg-amber-50',
    category: 'Query',
  },
  create_google_ads_pmax_campaign: {
    icon: FiTarget,
    label: 'Create PMax Campaign',
    color: 'tw-text-amber-600',
    bgColor: 'tw-bg-amber-50',
    category: 'Action',
  },
  create_google_ads_conversion: {
    icon: FiTarget,
    label: 'Create Conversion Action',
    color: 'tw-text-amber-600',
    bgColor: 'tw-bg-amber-50',
    category: 'Action',
  },
  modify_google_ads_conversion: {
    icon: FiTarget,
    label: 'Update Conversion Action',
    color: 'tw-text-amber-600',
    bgColor: 'tw-bg-amber-50',
    category: 'Action',
  },
  remove_google_ads_conversion: {
    icon: FiXCircle,
    label: 'Remove Conversion Action',
    color: 'tw-text-red-600',
    bgColor: 'tw-bg-red-50',
    category: 'Action',
  },
  get_facebook_ads_accounts: {
    icon: FiUsers,
    label: 'Facebook Ads Accounts',
    color: 'tw-text-blue-600',
    bgColor: 'tw-bg-blue-50',
    category: 'Query',
  },
  get_openai_ads_accounts: {
    icon: FiUsers,
    label: 'OpenAI Ads Account',
    color: 'tw-text-emerald-600',
    bgColor: 'tw-bg-emerald-50',
    category: 'Query',
  },
  search_openai_ads: {
    icon: BiSearch,
    label: 'OpenAI Ads Search',
    color: 'tw-text-emerald-600',
    bgColor: 'tw-bg-emerald-50',
    category: 'Query',
  },
  get_openai_ads_insights: {
    icon: FiTarget,
    label: 'OpenAI Ads Insights',
    color: 'tw-text-emerald-600',
    bgColor: 'tw-bg-emerald-50',
    category: 'Query',
  },
  update_openai_ads: {
    icon: FiTarget,
    label: 'Update OpenAI Ads',
    color: 'tw-text-emerald-600',
    bgColor: 'tw-bg-emerald-50',
    category: 'Action',
  },
  upload_openai_ads_creative: {
    icon: FiTarget,
    label: 'Upload OpenAI Ads Creative',
    color: 'tw-text-emerald-600',
    bgColor: 'tw-bg-emerald-50',
    category: 'Action',
  },
  upload_openai_ads_audience: {
    icon: FiUsers,
    label: 'Upload OpenAI Ads Audience',
    color: 'tw-text-emerald-600',
    bgColor: 'tw-bg-emerald-50',
    category: 'Action',
  },
  manage_openai_ads_conversions: {
    icon: FiTarget,
    label: 'OpenAI Ads Conversions',
    color: 'tw-text-emerald-600',
    bgColor: 'tw-bg-emerald-50',
    category: 'Action',
  },
  manage_openai_ads_account: {
    icon: FiTarget,
    label: 'OpenAI Ads Account Settings',
    color: 'tw-text-emerald-600',
    bgColor: 'tw-bg-emerald-50',
    category: 'Action',
  },
  bulk_openai_ads_mutations: {
    icon: FiTarget,
    label: 'OpenAI Ads Bulk Job',
    color: 'tw-text-emerald-600',
    bgColor: 'tw-bg-emerald-50',
    category: 'Action',
  },
  // Wix tools
  wix_get_product: {
    icon: BiPackage,
    label: 'Wix Product',
    color: 'tw-text-indigo-600',
    bgColor: 'tw-bg-indigo-50',
    category: 'Query',
  },
  wix_query_products: {
    icon: BiSearch,
    label: 'Wix Products Search',
    color: 'tw-text-indigo-600',
    bgColor: 'tw-bg-indigo-50',
    category: 'Query',
  },
  wix_create_product: {
    icon: BiPackage,
    label: 'Wix Create Product',
    color: 'tw-text-indigo-600',
    bgColor: 'tw-bg-indigo-50',
    category: 'Action',
  },
  wix_edit_product: {
    icon: BiPackage,
    label: 'Wix Edit Product',
    color: 'tw-text-indigo-600',
    bgColor: 'tw-bg-indigo-50',
    category: 'Action',
  },
  wix_delete_product: {
    icon: BiPackage,
    label: 'Wix Delete Product',
    color: 'tw-text-red-600',
    bgColor: 'tw-bg-red-50',
    category: 'Action',
  },
  // eBay tools
  ebay_get_inventory_item: {
    icon: BiPackage,
    label: 'eBay Inventory',
    color: 'tw-text-sky-600',
    bgColor: 'tw-bg-sky-50',
    category: 'Query',
  },
  ebay_get_offer: {
    icon: FiDollarSign,
    label: 'eBay Offer',
    color: 'tw-text-sky-600',
    bgColor: 'tw-bg-sky-50',
    category: 'Query',
  },
  ebay_get_offers: {
    icon: FiDollarSign,
    label: 'eBay Offers',
    color: 'tw-text-sky-600',
    bgColor: 'tw-bg-sky-50',
    category: 'Query',
  },
  ebay_get_listing: {
    icon: FiShoppingCart,
    label: 'eBay Listing',
    color: 'tw-text-sky-600',
    bgColor: 'tw-bg-sky-50',
    category: 'Query',
  },
  ebay_search_listings: {
    icon: BiSearch,
    label: 'eBay Search',
    color: 'tw-text-sky-600',
    bgColor: 'tw-bg-sky-50',
    category: 'Query',
  },
  ebay_create_product: {
    icon: BiPackage,
    label: 'eBay Create Product',
    color: 'tw-text-sky-600',
    bgColor: 'tw-bg-sky-50',
    category: 'Action',
  },
  ebay_create_listing: {
    icon: FiShoppingCart,
    label: 'eBay Create Listing',
    color: 'tw-text-sky-600',
    bgColor: 'tw-bg-sky-50',
    category: 'Action',
  },
  ebay_edit_listing: {
    icon: FiShoppingCart,
    label: 'eBay Edit Listing',
    color: 'tw-text-sky-600',
    bgColor: 'tw-bg-sky-50',
    category: 'Action',
  },
  ebay_delete_listing: {
    icon: FiShoppingCart,
    label: 'eBay Delete Listing',
    color: 'tw-text-red-600',
    bgColor: 'tw-bg-red-50',
    category: 'Action',
  },
  // Report tools
  get_orders_report: {
    icon: FiShoppingCart,
    label: 'Orders Report',
    color: 'tw-text-blue-600',
    bgColor: 'tw-bg-blue-50',
    category: 'Report',
  },
  get_line_items_report: {
    icon: FiShoppingCart,
    label: 'Line Items Report',
    color: 'tw-text-blue-600',
    bgColor: 'tw-bg-blue-50',
    category: 'Report',
  },
  get_charts_report: {
    icon: FiBarChart2,
    label: 'Charts Report',
    color: 'tw-text-indigo-600',
    bgColor: 'tw-bg-indigo-50',
    category: 'Report',
  },
  get_trends_report: {
    icon: FiTrendingUp,
    label: 'Trends Report',
    color: 'tw-text-cyan-600',
    bgColor: 'tw-bg-cyan-50',
    category: 'Report',
  },
  get_year_comparison_report: {
    icon: FiCalendar,
    label: 'Year Comparison',
    color: 'tw-text-amber-600',
    bgColor: 'tw-bg-amber-50',
    category: 'Report',
  },
  get_customers_report: {
    icon: FiUsers,
    label: 'Customers Report',
    color: 'tw-text-green-600',
    bgColor: 'tw-bg-green-50',
    category: 'Report',
  },
  get_products_report: {
    icon: BiPackage,
    label: 'Products Report',
    color: 'tw-text-purple-600',
    bgColor: 'tw-bg-purple-50',
    category: 'Report',
  },
  get_product_trends_report: {
    icon: BiLineChart,
    label: 'Product Trends',
    color: 'tw-text-purple-600',
    bgColor: 'tw-bg-purple-50',
    category: 'Report',
  },
  get_variants_report: {
    icon: BiPackage,
    label: 'Variants Report',
    color: 'tw-text-purple-600',
    bgColor: 'tw-bg-purple-50',
    category: 'Report',
  },
  get_brands_report: {
    icon: FiBriefcase,
    label: 'Brands Report',
    color: 'tw-text-teal-600',
    bgColor: 'tw-bg-teal-50',
    category: 'Report',
  },
  get_skus_report: {
    icon: BiPackage,
    label: 'SKUs Report',
    color: 'tw-text-slate-600',
    bgColor: 'tw-bg-slate-50',
    category: 'Report',
  },
  get_custom_order_bases_report: {
    icon: FiDatabase,
    label: 'Custom Report',
    color: 'tw-text-gray-600',
    bgColor: 'tw-bg-gray-100',
    category: 'Report',
  },
  get_marketing_report: {
    icon: FiTarget,
    label: 'Marketing Report',
    color: 'tw-text-pink-600',
    bgColor: 'tw-bg-pink-50',
    category: 'Report',
  },
  get_integrated_accounts: {
    icon: FiSettings,
    label: 'Integrations',
    color: 'tw-text-gray-600',
    bgColor: 'tw-bg-gray-100',
    category: 'Query',
  },
  // MMM tools
  get_mmm_summary: {
    icon: FiBarChart2,
    label: 'MMM Summary',
    color: 'tw-text-violet-600',
    bgColor: 'tw-bg-violet-50',
    category: 'Analytics',
  },
  get_mmm_channels: {
    icon: FiTrendingUp,
    label: 'MMM Channels',
    color: 'tw-text-violet-600',
    bgColor: 'tw-bg-violet-50',
    category: 'Analytics',
  },
  get_mmm_budget_scenarios: {
    icon: FiDollarSign,
    label: 'Budget Scenarios',
    color: 'tw-text-violet-600',
    bgColor: 'tw-bg-violet-50',
    category: 'Analytics',
  },
  run_mmm_simulation: {
    icon: BiTargetLock,
    label: 'MMM Simulation',
    color: 'tw-text-violet-600',
    bgColor: 'tw-bg-violet-50',
    category: 'Action',
  },
  // Knowledge tools (in-process, read-only)
  search_my_chats: {
    icon: BiSearch,
    label: 'Searching your past chats',
    color: 'tw-text-slate-600',
    bgColor: 'tw-bg-slate-50',
    category: 'Query',
  },
  read_chat: {
    icon: FiFile,
    label: 'Reading a past chat',
    color: 'tw-text-slate-600',
    bgColor: 'tw-bg-slate-50',
    category: 'Query',
  },
  search_my_tasks: {
    icon: BiSearch,
    label: 'Searching your scheduled tasks',
    color: 'tw-text-slate-600',
    bgColor: 'tw-bg-slate-50',
    category: 'Query',
  },
  read_task_run: {
    icon: FiCheckCircle,
    label: 'Reading a task result',
    color: 'tw-text-slate-600',
    bgColor: 'tw-bg-slate-50',
    category: 'Query',
  },
}

// Tool tooltips - concise descriptions shown on hover
const toolTooltips: Record<string, string> = {
  search: 'Search orders, customers, products, and more using natural language',
  fetch: 'Retrieve detailed record information by ID',
  insure_shipment: 'Add shipping insurance to a shipment',
  void_insurance: 'Cancel shipping insurance on a shipment',
  update_shipping_address: 'Update the shipping address for an order',
  update_auto_insurance_settings: 'Configure automatic insurance for future shipments',
  update_carrier_first_hundred_coverage: 'Set carrier first $100 coverage preference',
  update_carrier_insurance_rule: 'Configure insurance rules for specific carriers',
  get_current_shipping_settings: 'View current shipping and insurance configuration',
  export_to_email: 'Export data and send via email',
  export_to_google_drive: 'Export data to Google Drive',
  import_csv: 'Preview and import records from an uploaded file (CSV, Excel, JSON, or text)',
  export_to_csv: 'Download data as a CSV file',
  get_traffic_data: 'Retrieve website traffic and visitor analytics',
  get_traffic_metrics: 'Get traffic metrics like sessions, bounce rate, and conversions',
  get_ads_performance: 'View ad performance across Google Ads, Facebook, etc.',
  get_traffic_summary: 'High-level overview of traffic performance',
  modify_google_ads: 'Update Google Ads campaigns, budgets, or bids',
  modify_facebook_ads: 'Update Facebook Ads campaigns or targeting',
  search_google_ads: 'Search Google Ads campaigns and performance data',
  google_web_search: 'Search Google for public web results (competitors, news, market facts)',
  search_facebook_ads: 'Search Facebook Ads campaigns and metrics',
  get_google_ads_accounts:
    'List accessible Google Ads customer IDs per OAuth link; MCC metadata only if includeManagerDiscovery true',
  list_google_ads_client_accounts:
    'List client accounts under an MCC; use connectedGoogleEmail + managerCustomerId (after get_google_ads_accounts with discovery when matching a named manager)',
  create_google_ads_pmax_campaign: 'Create a Google Ads Performance Max campaign (requires user to attach images: landscape 1.91:1 min 600×314 and square 1:1 min 300×300)',
  create_google_ads_conversion: 'Create a new conversion action in Google Ads (goals, purchases, leads)',
  modify_google_ads_conversion: 'Update an existing Google Ads conversion action (name, status, value, lookback windows)',
  remove_google_ads_conversion: 'Remove a Google Ads conversion action (API mutate remove)',
  get_facebook_ads_accounts: 'List connected Facebook Ads accounts',
  get_openai_ads_accounts: 'List connected OpenAI Ads account',
  search_openai_ads: 'List OpenAI Ads campaigns, ad groups, ads, audiences, event settings, spend limits, or bulk jobs',
  get_openai_ads_insights: 'OpenAI Ads delivery and conversion insights',
  update_openai_ads: 'Create, update, pause, archive, or preview OpenAI Ads objects; delta-update product feeds',
  upload_openai_ads_creative: 'Upload OpenAI Ads creative images',
  upload_openai_ads_audience: 'Create, update, merge, or archive OpenAI Ads custom audiences',
  manage_openai_ads_conversions: 'Set up OpenAI Ads conversion tracking and send conversion events',
  manage_openai_ads_account: 'Update OpenAI Ads brand metadata and spending limits',
  bulk_openai_ads_mutations: 'Run OpenAI Ads bulk create/update jobs',
  wix_get_product: 'Get Wix product details',
  wix_query_products: 'Search and filter Wix store products',
  wix_create_product: 'Create a new product in Wix',
  wix_edit_product: 'Update an existing Wix product',
  wix_delete_product: 'Remove a product from Wix',
  ebay_get_inventory_item: 'Get eBay inventory item details by SKU',
  ebay_get_offer: 'Get eBay offer details',
  ebay_get_offers: 'List all offers for an eBay item',
  ebay_get_listing: 'Get eBay listing details',
  ebay_search_listings: 'Search across eBay listings',
  ebay_create_product: 'Create a new eBay product listing',
  ebay_create_listing: 'Create a new eBay listing with offer details',
  ebay_edit_listing: 'Update an existing eBay listing',
  ebay_delete_listing: 'Remove a listing from eBay',
  get_orders_report: 'Pre-built report of orders with revenue, counts, and date filters',
  get_line_items_report: 'Detailed line-item breakdown of orders with product-level data',
  get_charts_report: 'Chart-ready time series data for orders, revenue, and key metrics',
  get_trends_report: 'Period-over-period trend analysis showing growth and changes',
  get_year_comparison_report: 'Year-over-year comparison of key business metrics',
  get_customers_report: 'Customer analytics including new, returning, and lifetime value',
  get_products_report: 'Product performance report with sales, revenue, and rankings',
  get_product_trends_report: 'Product sales trends over time showing momentum and seasonality',
  get_variants_report: 'Variant-level performance data (sizes, colors, options)',
  get_brands_report: 'Brand-level sales and revenue breakdown',
  get_skus_report: 'SKU-level inventory and sales performance data',
  get_custom_order_bases_report: 'Custom report with flexible dimensions and metrics',
  get_marketing_report: 'Marketing performance across all channels with attribution data',
  get_integrated_accounts: 'List all connected platform integrations and their status',
  get_mmm_summary: 'Overview of Marketing Mix Model results and channel attribution',
  get_mmm_channels: 'Per-channel MMM metrics, ROAS, and contribution data',
  get_mmm_budget_scenarios: 'Pre-computed budget reallocation scenarios for optimization',
  run_mmm_simulation: 'Run a custom budget simulation to forecast outcomes',
  search_my_chats: 'Search your past PayHelm AI chats for relevant context',
  read_chat: 'Read the full transcript of one of your past chats',
  search_my_tasks: 'Search your scheduled tasks for relevant context',
  read_task_run: 'Read the result of a past scheduled task run',
}

// Strip MCP prefix from tool name for display and lookup
const stripMcpPrefix = (toolName: string): string => {
  // Remove mcp__payhelm__, mcp__custom__, mcp__knowledge__, or payhelm__ prefix if present
  return toolName.replace(/^(mcp__(payhelm|custom|knowledge)__|payhelm__)/, '')
}

// Check if a tool is a PayHelm MCP tool (not internal SDK tools like Read, Bash, etc.)
const isMcpTool = (toolName: string): boolean => {
  if (!toolName) return false
  // MCP tools have prefixes like mcp__payhelm__, mcp__custom__, mcp__knowledge__, or payhelm__
  if (
    toolName.startsWith('mcp__payhelm__') ||
    toolName.startsWith('mcp__custom__') ||
    toolName.startsWith('mcp__knowledge__') ||
    toolName.startsWith('payhelm__')
  )
    return true
  // Also allow known PayHelm tool names without prefix
  const knownPayhelmTools = [
    'search', 'fetch', 'get_traffic_data', 'get_traffic_metrics', 'get_traffic_summary',
    'get_ads_performance', 'get_google_ads_accounts', 'list_google_ads_client_accounts', 'get_facebook_ads_accounts', 'get_openai_ads_accounts',
    'search_openai_ads', 'update_openai_ads', 'upload_openai_ads_creative', 'upload_openai_ads_audience', 'get_openai_ads_insights',
    'manage_openai_ads_conversions', 'manage_openai_ads_account', 'bulk_openai_ads_mutations',
    'search_google_ads', 'search_facebook_ads', 'modify_google_ads', 'modify_facebook_ads',
    'create_google_ads_pmax_campaign', 'create_google_ads_conversion', 'modify_google_ads_conversion', 'remove_google_ads_conversion',
    'get_klaviyo_accounts', 'search_klaviyo', 'insure_shipment', 'void_insurance',
    'export_to_email', 'export_to_csv', 'export_to_google_drive',
    'wix_get_product', 'wix_query_products', 'wix_create_product', 'wix_edit_product', 'wix_delete_product',
    'ebay_get_inventory_item', 'ebay_get_offer', 'ebay_get_offers', 'ebay_get_listing', 'ebay_search_listings',
    'ebay_create_product', 'ebay_create_listing', 'ebay_edit_listing', 'ebay_delete_listing',
    'get_mmm_summary', 'get_mmm_channels', 'get_mmm_budget_scenarios', 'run_mmm_simulation',
    'get_orders_report', 'get_line_items_report', 'get_charts_report', 'get_trends_report',
    'get_year_comparison_report', 'get_customers_report', 'get_products_report', 'get_product_trends_report',
    'get_variants_report', 'get_brands_report', 'get_skus_report', 'get_custom_order_bases_report',
    'get_marketing_report', 'get_integrated_accounts',
    'search_my_chats', 'read_chat', 'search_my_tasks', 'read_task_run',
  ]
  return knownPayhelmTools.includes(toolName.toLowerCase())
}

// Check if a tool is an internal Anthropic SDK tool (Read, Bash, Grep, Glob, etc.)
// These should be grouped into a compact tiled display
// IMPORTANT: Must not match MCP tools - check MCP first, then use exact name matching
const isSdkTool = (toolName: string): boolean => {
  if (!toolName) return false
  // Never classify MCP tools as SDK tools
  if (isMcpTool(toolName)) return false
  const sdkTools = [
    'read', 'write', 'edit', 'bash', 'grep', 'glob', 'list', 'view',
    'str_replace_editor', 'computer', 'text_editor',
  ]
  const lower = toolName.toLowerCase()
  return sdkTools.some(t => lower === t)
}

// Get icon for SDK tools
const getSdkToolIcon = (toolName: string): React.ComponentType<any> => {
  const lower = toolName.toLowerCase()
  if (lower.includes('bash') || lower.includes('terminal')) return FiTerminal
  if (lower.includes('read') || lower.includes('view') || lower.includes('file')) return FiFile
  if (lower.includes('grep') || lower.includes('search')) return BiSearch
  if (lower.includes('edit') || lower.includes('write') || lower.includes('str_replace')) return FiCode
  if (lower.includes('glob') || lower.includes('list')) return FiFile
  return FiCode
}

// Get description for SDK tool
const getSdkToolDescription = (tool: string, input: any): string => {
  const lower = tool.toLowerCase()
  if (lower.includes('read')) return `Read ${input?.file_path?.split('/').pop() || 'file'}`
  if (lower.includes('bash')) return `Run command`
  if (lower.includes('grep')) return `Search: ${input?.pattern?.slice(0, 30) || 'pattern'}`
  if (lower.includes('glob')) return `Find files: ${input?.pattern?.slice(0, 30) || 'pattern'}`
  if (lower.includes('edit') || lower.includes('write')) return `Edit ${input?.file_path?.split('/').pop() || 'file'}`
  return tool
}

// Get tool metadata with fallback
const getToolMeta = (
  toolName: string
): {
  icon: React.ComponentType<any>
  label: string
  color: string
  bgColor: string
  category: string
  tooltip?: string
} => {
  // Strip MCP prefix for lookup
  const shortName = stripMcpPrefix(toolName)

  if (toolMetadata[shortName]) {
    return { ...toolMetadata[shortName], tooltip: toolTooltips[shortName] }
  }

  // Fallback based on name patterns
  const name = shortName.toLowerCase()
  if (name.includes('search')) return { ...toolMetadata.search, label: shortName.replace(/_/g, ' ') }
  if (name.includes('traffic') || name.includes('analytics')) return { ...toolMetadata.get_traffic_data, label: shortName.replace(/_/g, ' ') }
  if (name.includes('ship') || name.includes('carrier')) return { ...toolMetadata.get_current_shipping_settings, label: shortName.replace(/_/g, ' ') }
  if (name.includes('insur')) return { ...toolMetadata.insure_shipment, label: shortName.replace(/_/g, ' ') }
  if (name.includes('export') || name.includes('email')) return { ...toolMetadata.export_to_email, label: shortName.replace(/_/g, ' ') }
  if (name.includes('mmm') || name.includes('mix_model')) return { ...toolMetadata.get_mmm_summary, label: shortName.replace(/_/g, ' '), tooltip: toolTooltips[shortName] }
  if (name.includes('report')) return { ...toolMetadata.get_orders_report, label: shortName.replace(/_/g, ' '), tooltip: toolTooltips[shortName] }

  return {
    icon: FiDatabase,
    label: shortName.replace(/_/g, ' '),
    color: 'tw-text-gray-600',
    bgColor: 'tw-bg-gray-100',
    category: 'Query',
  }
}

// Detect data source from query
const detectDataSource = (query: unknown, tool: unknown): { icon: React.ComponentType<any>; label: string; color: string } | null => {
  const q = (typeof query === 'string' ? query : String(query ?? '')).toLowerCase()
  const t = stripMcpPrefix(typeof tool === 'string' ? tool : String(tool ?? '')).toLowerCase()

  if (q.includes('order') || q.includes('sales') || q.includes('revenue')) {
    return { icon: FiShoppingCart, label: 'Orders', color: 'tw-text-blue-500' }
  }
  if (q.includes('customer') || q.includes('buyer')) {
    return { icon: FiUsers, label: 'Customers', color: 'tw-text-green-500' }
  }
  if (q.includes('product') || q.includes('inventory') || q.includes('sku')) {
    return { icon: BiPackage, label: 'Products', color: 'tw-text-purple-500' }
  }
  if (q.includes('traffic') || q.includes('visitor') || q.includes('session') || t.includes('traffic')) {
    return { icon: BiWorld, label: 'Traffic', color: 'tw-text-cyan-500' }
  }
  if (q.includes('shipping') || q.includes('carrier') || q.includes('insurance') || t.includes('ship') || t.includes('insur')) {
    return { icon: FiTruck, label: 'Shipping', color: 'tw-text-orange-500' }
  }
  if (q.includes('ads') || q.includes('campaign') || q.includes('marketing') || t.includes('ads') || t.includes('marketing_report')) {
    return { icon: FiTarget, label: 'Marketing', color: 'tw-text-pink-500' }
  }
  if (t.includes('_report') && !t.includes('marketing')) {
    return { icon: FiBarChart2, label: 'Reports', color: 'tw-text-indigo-500' }
  }
  if (q.includes('cost') || q.includes('profit') || q.includes('margin') || q.includes('aov')) {
    return { icon: FiDollarSign, label: 'Finance', color: 'tw-text-emerald-500' }
  }
  if (q.includes('mmm') || q.includes('mix model') || q.includes('attribution') || t.includes('mmm')) {
    return { icon: FiBarChart2, label: 'MMM', color: 'tw-text-violet-500' }
  }

  return null
}

// Format tool input for display - returns primary description
const formatToolQuery = (input: any): string => {
  if (!input) return ''
  // Always coerce to a string — some tools pass a structured object for `query`
  // (e.g. a Solr/filter object), and returning it raw makes React throw
  // "Objects are not valid as a React child" when it's rendered.
  const asText = (v: any): string => (typeof v === 'string' ? v : v == null ? '' : JSON.stringify(v))
  if (input.query) return asText(input.query)
  if (input.report_title) return asText(input.report_title)
  if (input.document_id) return `Document: ${asText(input.document_id)}`
  if (input.order_id) return `Order #${asText(input.order_id)}`
  if (input.recipient_email) return `To: ${asText(input.recipient_email)}`
  if (input.file_name) return `File: ${asText(input.file_name)}`
  return ''
}

// Extract parameters as structured data for display
const extractToolParams = (input: any): { key: string; value: string; icon?: React.ComponentType<any> }[] => {
  if (!input) return []
  const params: { key: string; value: string; icon?: React.ComponentType<any> }[] = []

  // Skip these keys as they're shown in the main query
  const skipKeys = ['query', 'report_title', 'document_id', 'order_id', 'recipient_email', 'file_name']

  for (const [key, value] of Object.entries(input)) {
    if (skipKeys.includes(key) || value === null || value === undefined) continue

    // Format the value nicely - handle objects/arrays properly
    let displayValue: string
    if (typeof value === 'object') {
      // For objects/arrays, show a compact summary
      if (Array.isArray(value)) {
        displayValue = value.length === 1
          ? (typeof value[0] === 'object' ? JSON.stringify(value[0]) : String(value[0]))
          : `${value.length} items`
      } else {
        // For objects, show key summary or stringify if small
        const jsonStr = JSON.stringify(value)
        displayValue = jsonStr.length > 50 ? `{${Object.keys(value).join(', ')}}` : jsonStr
      }
    } else {
      displayValue = String(value)
    }
    let icon: React.ComponentType<any> | undefined = undefined

    if (key.includes('date')) {
      icon = FiCalendar
      // Format date nicely if it's a date string
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        displayValue = formatCalendarDate(value, { month: 'short', day: 'numeric', year: 'numeric' })
      }
    }

    // Format account/customer IDs nicely (e.g., 2044948376 → 204-494-8376)
    if (key.toLowerCase().includes('customer') || key.toLowerCase().includes('account_id') || key.toLowerCase().includes('customerid')) {
      const numStr = String(value).replace(/\D/g, '')
      if (numStr.length === 10) {
        displayValue = `${numStr.slice(0, 3)}-${numStr.slice(3, 6)}-${numStr.slice(6)}`
      }
    }

    // Format the key nicely (snake_case to Title Case)
    let displayKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    // Rename "Customerid" to "Account"
    if (displayKey.toLowerCase() === 'customerid') displayKey = 'Account'

    params.push({ key: displayKey, value: displayValue, icon })
  }

  return params
}

// Extract a human-readable description from any tool input
const extractInputDescription = (input: any): string => {
  if (!input) return ''
  // Check common query-like fields
  if (input.query) return input.query
  if (input.q) return input.q
  if (input.search_query) return input.search_query
  if (input.prompt) return input.prompt
  if (input.report_title) return input.report_title
  if (input.document_id) return `Document: ${input.document_id}`
  if (input.order_id) return `Order #${input.order_id}`
  if (input.recipient_email) return `To: ${input.recipient_email}`
  if (input.file_name) return `File: ${input.file_name}`
  // Handle create_google_ads_pmax_campaign (campaign name, budget, etc.)
  if (input.campaignName) return `PMax: ${input.campaignName}`
  // Handle modify/action tool inputs with nested operations (e.g., modify_google_ads)
  if (input.operations && Array.isArray(input.operations)) {
    const ops = input.operations
    const opTypes = ops.map((op: any) => op.operationType || op.type || '').filter(Boolean)
    // Try to extract names from nested create/update operations
    const names: string[] = []
    for (const op of ops) {
      const innerOps = op.operations || op.items || []
      if (Array.isArray(innerOps)) {
        for (const inner of innerOps) {
          const target = inner.create || inner.update || inner
          if (target?.name) names.push(target.name)
          if (target?.campaignName) names.push(target.campaignName)
        }
      }
    }
    if (opTypes.length > 0) {
      const typeStr = opTypes.join(', ')
      const nameStr = names.length > 0 ? `: ${names[0]}${names.length > 1 ? ` +${names.length - 1}` : ''}` : ''
      return `${typeStr}${nameStr}`
    }
    // If no operationType found, show names or operation count
    if (names.length > 0) return names[0] + (names.length > 1 ? ` +${names.length - 1} more` : '')
    return `${ops.length} operation${ops.length !== 1 ? 's' : ''}`
  }
  // Fallback: find the first meaningful string value in the input (skip IDs first pass)
  for (const [key, value] of Object.entries(input)) {
    if (typeof value === 'string' && value.length > 3 && value.length < 200) {
      if (key.toLowerCase().includes('id') || key.toLowerCase().includes('customer')) continue
      return value
    }
  }
  // Second pass: include ID-like fields if nothing else found
  for (const [key, value] of Object.entries(input)) {
    if (typeof value === 'string' && value.length > 3 && value.length < 200) {
      return `${key}: ${value}`
    }
  }
  return ''
}

// Get a human-readable action description for the tool
const getToolActionDescription = (tool: string, input: any): string => {
  const shortName = stripMcpPrefix(tool)
  const t = shortName.toLowerCase()
  const desc = extractInputDescription(input)

  if (t.includes('search')) return desc ? `Search: ${desc}` : 'Searching database...'
  if (t.includes('fetch')) return desc ? `Fetch: ${desc}` : 'Fetching data...'
  if (t.includes('traffic_summary')) return desc || 'Fetching traffic analytics'
  if (t.includes('traffic_data')) return desc || 'Retrieving traffic data'
  if (t.includes('traffic_metrics')) return desc || 'Loading traffic metrics'
  if (t.includes('ads_performance')) return desc || 'Analyzing ad performance'
  if (t.includes('shipping_settings')) return desc || 'Loading shipping configuration'
  if (t.includes('insure')) return desc || 'Processing insurance request'
  if (t.includes('export')) return desc || 'Preparing export'
  // Modify/action tools - show what's being modified (specific tools before generic modify_google_ads substring match)
  if (t.includes('remove_google_ads_conversion')) return desc ? `Remove conversion action: ${desc}` : 'Removing Google Ads conversion action'
  if (t.includes('modify_google_ads_conversion')) return desc ? `Update conversion action: ${desc}` : 'Updating Google Ads conversion action'
  if (t.includes('modify_google_ads')) return desc ? `Modify Google Ads: ${desc}` : 'Modifying Google Ads'
  if (t.includes('create_google_ads_conversion')) return desc ? `Create conversion action: ${desc}` : 'Creating Google Ads conversion action'
  if (t.includes('create_google_ads_pmax_campaign')) return desc ? `Create PMax campaign: ${desc}` : 'Creating Performance Max campaign'
  if (t.includes('modify_facebook_ads')) return desc ? `Modify Facebook Ads: ${desc}` : 'Modifying Facebook Ads'
  if (t.includes('modify')) return desc ? `Modify: ${desc}` : 'Applying modifications'
  if (
    t.includes('get_google_ads_accounts') ||
    t.includes('list_google_ads_client_accounts') ||
    t.includes('get_facebook_ads_accounts') ||
    t.includes('get_openai_ads_accounts')
  )
    return 'Fetching ad accounts'
  if (t.includes('update')) return desc || 'Applying changes'

  return desc || shortName.replace(/_/g, ' ')
}

// Check if text looks like a final summary
const isSummaryText = (text: string): boolean => {
  const hasHeaders = /##\s|^\*\*[A-Z]|\*\*[A-Z][^*]+\*\*:/.test(text)
  const hasBullets = /^\s*[-•*]\s|^\s*\d+\.\s/m.test(text)
  const isLong = text.length > 300
  return isLong && (hasHeaders || hasBullets)
}

// Extract a concise summary from long thinking text
const extractConciseSummary = (text: string): string => {
  const summaryMatch = text.match(/##\s*Summary[:\s]*([^#]+)/i)
  if (summaryMatch) {
    const summary = summaryMatch[1].replace(/\*\*/g, '').trim()
    const sentences = summary.split(/[.!?]/).filter(s => s.trim().length > 10).slice(0, 2)
    if (sentences.length > 0) {
      return sentences.join('. ').trim() + '.'
    }
  }

  const cleanText = text.replace(/\*\*/g, '').replace(/##[^#\n]+/g, '').trim()
  const firstSentences = cleanText.split(/[.!?]/).filter(s => s.trim().length > 10).slice(0, 2)
  if (firstSentences.length > 0) {
    return firstSentences.join('. ').trim() + '.'
  }

  return cleanText.slice(0, 150) + '...'
}

// Count key findings/action items in summary
const countKeyItems = (text: string): { findings: number; actions: number } => {
  const findingsMatch = text.match(/Key Findings|## Findings|## Results/i)
  const actionsMatch = text.match(/Action Items|## Actions|## Recommendations/i)

  let findings = 0
  let actions = 0

  if (findingsMatch) {
    const afterFindings = text.slice(text.indexOf(findingsMatch[0]))
    findings = (afterFindings.match(/^\s*\d+\./gm) || []).length
  }

  if (actionsMatch) {
    const afterActions = text.slice(text.indexOf(actionsMatch[0]))
    actions = (afterActions.match(/^\s*\d+\./gm) || []).length
  }

  return { findings, actions }
}

// Thinking step component
const ThinkingStep: React.FC<{ thought: string; isLast: boolean; stepNumber: number }> = ({ thought, isLast, stepNumber }) => {
  const [expanded, setExpanded] = useState(false)
  const isSummary = isSummaryText(thought)
  const isPlan = isPlanText(thought)
  const conciseSummary = isSummary ? extractConciseSummary(thought) : thought
  const { findings, actions } = isSummary ? countKeyItems(thought) : { findings: 0, actions: 0 }
  const isLongThought = thought.length > 200

  // Parse plan items if this is a plan
  const planItems = isPlan ? extractPlanItems(thought) : []

  const displayText = isSummary
    ? conciseSummary
    : isPlan
      ? thought.replace(/\*\*📋 Plan:\*\*|📋 Plan:|Plan:/gi, '').trim()
      : (isLongThought && !expanded ? thought.slice(0, 180) + '...' : thought)

  // Plan step - special styling with indigo theme
  if (isPlan) {
    return (
      <div className={`tw-flex tw-gap-3 ${isLast ? '' : 'tw-pb-4'}`}>
        <div className="tw-flex tw-flex-col tw-items-center">
          <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-shadow-sm tw-bg-gradient-to-br tw-from-indigo-500 tw-to-purple-600">
            <BiTargetLock className="tw-text-white" size={16} />
          </div>
          {!isLast && <div className="tw-w-0.5 tw-flex-1 tw-bg-gradient-to-b tw-from-gray-200 tw-to-transparent tw-mt-2" />}
        </div>
        <div className="tw-flex-1 tw-min-w-0">
          <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
            <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-px-2.5 tw-py-1 tw-rounded-md tw-bg-indigo-100 tw-text-indigo-700">
              <BiTargetLock size={12} />
              Analysis Plan
            </span>
            {planItems.length > 0 && (
              <span className="tw-text-xs tw-text-gray-500 tw-font-medium">{planItems.length} steps</span>
            )}
          </div>
          <div className="tw-rounded-lg tw-bg-gradient-to-br tw-from-indigo-50 tw-to-purple-50 tw-border tw-border-indigo-100 tw-overflow-hidden">
            {planItems.length > 0 ? (
              <div className="tw-divide-y tw-divide-indigo-100">
                {planItems.map((item, idx) => (
                  <div key={idx} className="tw-flex tw-items-start tw-gap-3 tw-px-3 tw-py-2.5">
                    <span className="tw-flex-shrink-0 tw-w-5 tw-h-5 tw-rounded-full tw-bg-indigo-100 tw-text-indigo-600 tw-text-xs tw-font-semibold tw-flex tw-items-center tw-justify-center">
                      {idx + 1}
                    </span>
                    <span className="tw-text-sm tw-text-gray-700">{item.replace(/^\d+\.\s*/, '')}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="tw-px-3 tw-py-2.5">
                <p className="tw-text-sm tw-text-gray-700 tw-leading-relaxed">{displayText}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Summary/completion step
  if (isSummary) {
    return (
      <div className={`tw-flex tw-gap-3 ${isLast ? '' : 'tw-pb-4'}`}>
        <div className="tw-flex tw-flex-col tw-items-center">
          <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-shadow-sm tw-bg-gradient-to-br tw-from-emerald-400 tw-to-green-500">
            <FiCheckCircle className="tw-text-white" size={16} />
          </div>
          {!isLast && <div className="tw-w-0.5 tw-flex-1 tw-bg-gradient-to-b tw-from-gray-200 tw-to-transparent tw-mt-2" />}
        </div>
        <div className="tw-flex-1 tw-min-w-0">
          <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
            <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-px-2.5 tw-py-1 tw-rounded-md tw-bg-emerald-100 tw-text-emerald-700">
              <FiCheckCircle size={12} />
              Analysis Complete
            </span>
          </div>
          <div className="tw-rounded-lg tw-px-3 tw-py-2.5 tw-bg-emerald-50 tw-border tw-border-emerald-100">
            <div className="tw-text-sm tw-text-gray-700 tw-leading-relaxed tw-prose tw-prose-sm tw-max-w-none"><ReactMarkdown remarkPlugins={[remarkGfm]}>{displayText}</ReactMarkdown></div>
            {(findings > 0 || actions > 0) && (
              <div className="tw-flex tw-gap-2 tw-mt-2.5 tw-pt-2.5 tw-border-t tw-border-emerald-100">
                {findings > 0 && (
                  <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-px-2 tw-py-1 tw-bg-blue-100 tw-text-blue-700 tw-rounded-md tw-font-medium">
                    <BiLineChart size={12} />
                    {findings} findings
                  </span>
                )}
                {actions > 0 && (
                  <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-px-2 tw-py-1 tw-bg-amber-100 tw-text-amber-700 tw-rounded-md tw-font-medium">
                    <FiTarget size={12} />
                    {actions} actions
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Regular reasoning step
  return (
    <div className={`tw-flex tw-gap-3 ${isLast ? '' : 'tw-pb-4'}`}>
      <div className="tw-flex tw-flex-col tw-items-center">
        <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-shadow-sm tw-bg-gradient-to-br tw-from-slate-400 tw-to-slate-500">
          <BiBrain className="tw-text-white" size={16} />
        </div>
        {!isLast && <div className="tw-w-0.5 tw-flex-1 tw-bg-gradient-to-b tw-from-gray-200 tw-to-transparent tw-mt-2" />}
      </div>
      <div className="tw-flex-1 tw-min-w-0">
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
          <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-px-2.5 tw-py-1 tw-rounded-md tw-bg-slate-100 tw-text-slate-600">
            <BiBrain size={12} />
            Thinking
          </span>
        </div>
        <div className="tw-text-sm tw-text-gray-600 tw-leading-relaxed tw-prose tw-prose-sm tw-max-w-none"><ReactMarkdown remarkPlugins={[remarkGfm]}>{displayText}</ReactMarkdown></div>
        {isLongThought && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="tw-text-xs tw-text-slate-500 hover:tw-text-slate-700 tw-font-medium tw-mt-1.5"
          >
            {expanded ? '← Show less' : 'Show more →'}
          </button>
        )}
      </div>
    </div>
  )
}

// Tool call step component
const ToolStep: React.FC<{
  tool: string
  input: any
  status: 'running' | 'completed' | 'failed'
  isLast: boolean
  stepNumber: number
}> = ({ tool, input, status, isLast, stepNumber }) => {
  const meta = getToolMeta(tool)
  const Icon = meta.icon
  const actionDescription = getToolActionDescription(tool, input)
  const params = extractToolParams(input)
  const isRunning = status === 'running'
  const dataSource = detectDataSource(input?.query || '', tool)

  return (
    <div className={`tw-flex tw-gap-3 ${isLast ? '' : 'tw-pb-3'}`} title={meta.tooltip}>
      <div className="tw-flex tw-flex-col tw-items-center">
        <div className={`tw-w-8 tw-h-8 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-shadow-sm ${
          isRunning
            ? 'tw-bg-gradient-to-br tw-from-blue-400 tw-to-indigo-500'
            : status === 'completed'
            ? 'tw-bg-gradient-to-br tw-from-emerald-400 tw-to-green-500'
            : 'tw-bg-gradient-to-br tw-from-red-400 tw-to-rose-500'
        }`}>
          {isRunning ? (
            <BiLoaderAlt className="tw-animate-spin tw-text-white" size={16} />
          ) : status === 'completed' ? (
            <FiCheckCircle className="tw-text-white" size={16} />
          ) : (
            <Icon className="tw-text-white" size={16} />
          )}
        </div>
        {!isLast && <div className="tw-w-0.5 tw-flex-1 tw-bg-gradient-to-b tw-from-gray-200 tw-to-gray-100 tw-mt-2" />}
      </div>
      <div className="tw-flex-1 tw-min-w-0">
        {/* Header row with tool info */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1.5 tw-flex-wrap">
          <span className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-px-2.5 tw-py-1 tw-rounded-md ${meta.bgColor} ${meta.color}`}>
            <Icon size={12} />
            {meta.label}
          </span>
          {dataSource && (
            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600 tw-bg-gray-100 tw-px-2 tw-py-1 tw-rounded-md tw-font-medium">
              <dataSource.icon size={11} className={dataSource.color} />
              {dataSource.label}
            </span>
          )}
          {isRunning && (
            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-blue-600 tw-font-medium">
              <BiLoaderAlt className="tw-animate-spin" size={12} />
              Running
            </span>
          )}
          {status === 'completed' && (
            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-emerald-600 tw-font-medium">
              <FiCheckCircle size={12} />
              Done
            </span>
          )}
        </div>

        {/* Action description */}
        <p className="tw-text-sm tw-text-gray-700 tw-mb-2">
          {actionDescription}
        </p>

        {/* Parameters as individual chips */}
        {params.length > 0 && (
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {params.map((param, idx) => {
              const ParamIcon = param.icon
              return (
                <div
                  key={idx}
                  className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-bg-slate-100 tw-text-slate-600 tw-px-2.5 tw-py-1.5 tw-rounded-lg tw-border tw-border-slate-200"
                >
                  {ParamIcon && <ParamIcon size={12} className="tw-text-slate-500" />}
                  <span className="tw-font-medium tw-text-slate-500">{param.key}:</span>
                  <span className="tw-text-slate-700">{param.value}</span>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// Memory update step component
const MemoryStep: React.FC<{ update: MemoryUpdate; isLast: boolean }> = ({ update, isLast }) => (
  <div className={`tw-flex tw-gap-3 ${isLast ? '' : 'tw-pb-3'}`}>
    <div className="tw-flex tw-flex-col tw-items-center">
      <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-gradient-to-br tw-from-sky-400 tw-to-blue-500 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-shadow-sm">
        <BiMemoryCard className="tw-text-white" size={16} />
      </div>
      {!isLast && <div className="tw-w-0.5 tw-flex-1 tw-bg-gradient-to-b tw-from-gray-200 tw-to-gray-100 tw-mt-2" />}
    </div>
    <div className="tw-flex-1 tw-min-w-0">
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
        <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-sky-600">
          Memory Optimized
        </span>
      </div>
      <div className="tw-rounded-lg tw-px-3 tw-py-2 tw-bg-sky-50 tw-border tw-border-sky-100">
        <p className="tw-text-sm tw-text-gray-700">
          Context summarized for efficiency — saved {Math.round(update.savedChars / 1000)}KB
        </p>
      </div>
    </div>
  </div>
)

// Expandable thinking row for the list view — shows AI reasoning/analysis
const ThinkingRow: React.FC<{ thought: string }> = ({ thought }) => {
  const [expanded, setExpanded] = useState(false)
  const isPlan = isPlanText(thought)
  const isSummary = isSummaryText(thought)
  const isLong = thought.length > 120

  // Clean up markdown formatting for preview
  const cleanForPreview = (text: string): string => {
    return text
      .replace(/\*\*/g, '')           // Remove bold markers
      .replace(/^#+\s*/gm, '')        // Remove heading markers
      .replace(/^\s*[-•*]\s*/gm, ' ') // Flatten bullet points
      .replace(/\n+/g, ' ')           // Collapse newlines to spaces
      .replace(/\s{2,}/g, ' ')        // Collapse multiple spaces
      .trim()
  }

  // For plans, use shared extraction (filters non-actionable narrative steps)
  const planItems = isPlan ? extractPlanItems(thought) : []

  const previewText = isPlan
    ? (planItems.length > 0 ? `Plan: ${planItems[0]}${planItems.length > 1 ? ` (+${planItems.length - 1} more steps)` : ''}` : 'Analysis plan')
    : isSummary
    ? cleanForPreview(thought).slice(0, 150) + (thought.length > 150 ? '...' : '')
    : cleanForPreview(thought).slice(0, 150) + (thought.length > 150 ? '...' : '')

  const iconBg = isPlan ? 'tw-bg-indigo-100' : isSummary ? 'tw-bg-emerald-100' : 'tw-bg-slate-100'
  const Icon = isPlan ? BiTargetLock : isSummary ? FiCheckCircle : BiBrain
  const iconColor = isPlan ? 'tw-text-indigo-600' : isSummary ? 'tw-text-emerald-600' : 'tw-text-slate-500'

  if (!isLong && !isPlan) {
    // Short thought — just show it inline, no expand needed
    return (
      <div className="tw-flex tw-gap-3 tw-px-3 tw-py-2.5">
        <div className={`tw-w-6 tw-h-6 tw-rounded tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-mt-0.5 ${iconBg}`}>
          <Icon className={iconColor} size={14} />
        </div>
        <div className="tw-text-sm tw-text-gray-600 tw-leading-relaxed tw-prose tw-prose-sm tw-max-w-none"><ReactMarkdown remarkPlugins={[remarkGfm]}>{thought}</ReactMarkdown></div>
      </div>
    )
  }

  return (
    <div className="tw-px-3 tw-py-2.5">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="tw-w-full tw-flex tw-gap-3 tw-text-left tw-group"
      >
        <div className={`tw-w-6 tw-h-6 tw-rounded tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-mt-0.5 ${iconBg}`}>
          <Icon className={iconColor} size={14} />
        </div>
        <div className="tw-flex-1 tw-min-w-0">
          {expanded ? (
            <BiChevronDown size={14} className="tw-text-gray-400 tw-float-right tw-mt-0.5" />
          ) : (
            <BiChevronRight size={14} className="tw-text-gray-400 tw-float-right tw-mt-0.5 group-hover:tw-text-gray-600" />
          )}
          <p className="tw-text-sm tw-text-gray-600 tw-leading-relaxed">{expanded ? '' : previewText}</p>
        </div>
      </button>

      {expanded && (
        <div className={`tw-ml-9 tw-mt-2 tw-rounded-lg tw-px-3 tw-py-3 tw-border ${
          isPlan ? 'tw-bg-indigo-50 tw-border-indigo-100' : isSummary ? 'tw-bg-emerald-50 tw-border-emerald-100' : 'tw-bg-slate-50 tw-border-slate-100'
        }`}>
          {isPlan && planItems.length > 0 ? (
            <div className="tw-space-y-1.5">
              {planItems.map((item, i) => (
                <div key={i} className="tw-flex tw-gap-2 tw-items-start">
                  <span className="tw-flex-shrink-0 tw-w-5 tw-h-5 tw-rounded-full tw-bg-indigo-100 tw-text-indigo-600 tw-text-xs tw-font-semibold tw-flex tw-items-center tw-justify-center">
                    {i + 1}
                  </span>
                  <span className="tw-text-sm tw-text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="tw-text-sm tw-text-gray-700 tw-leading-relaxed tw-prose tw-prose-sm tw-max-w-none"><ReactMarkdown remarkPlugins={[remarkGfm]}>{thought}</ReactMarkdown></div>
          )}
        </div>
      )}
    </div>
  )
}

// SDK tool info for compact display
interface SdkToolInfo {
  tool: string
  toolInput?: any
  status: 'running' | 'completed' | 'failed'
}

// Compact SDK tools row component - Replit style
const SdkToolsRow: React.FC<{
  tools: SdkToolInfo[]
  completedCount: number
  isRunning: boolean
}> = ({ tools, completedCount, isRunning }) => {
  const [expanded, setExpanded] = useState(false)

  if (tools.length === 0) return null

  // Collapsed view - tiled icons
  if (!expanded) {
    return (
      <div className="tw-px-3 tw-py-2">
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="tw-inline-flex tw-items-center tw-gap-2 tw-px-2.5 tw-py-1.5 tw-rounded-md tw-bg-gray-100 hover:tw-bg-gray-200 tw-transition-colors tw-text-left"
        >
          {/* Tiled icons */}
          <div className="tw-flex tw-items-center tw-gap-0.5">
            {isRunning && (
              <div className="tw-w-5 tw-h-5 tw-rounded tw-bg-blue-100 tw-flex tw-items-center tw-justify-center">
                <BiLoaderAlt className="tw-animate-spin tw-text-blue-600" size={12} />
              </div>
            )}
            {tools.slice(0, 6).map((t, idx) => {
              const Icon = getSdkToolIcon(t.tool)
              return (
                <div
                  key={idx}
                  className="tw-w-5 tw-h-5 tw-rounded tw-bg-gray-200 tw-flex tw-items-center tw-justify-center"
                >
                  <Icon className="tw-text-gray-500" size={12} />
                </div>
              )
            })}
            {tools.length > 6 && (
              <div className="tw-w-5 tw-h-5 tw-rounded tw-bg-gray-200 tw-flex tw-items-center tw-justify-center">
                <span className="tw-text-[9px] tw-font-medium tw-text-gray-500">+{tools.length - 6}</span>
              </div>
            )}
          </div>
          <span className="tw-text-xs tw-text-gray-500">
            {isRunning ? `${completedCount}/${tools.length}` : tools.length} {tools.length === 1 ? 'action' : 'actions'}
          </span>
        </button>
      </div>
    )
  }

  // Expanded view - list of SDK tools
  return (
    <div className="tw-border-b tw-border-gray-100">
      <button
        type="button"
        onClick={() => setExpanded(false)}
        className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-left hover:tw-bg-gray-50 tw-transition-colors"
      >
        <BiChevronDown size={14} className="tw-text-gray-400" />
        <span className="tw-text-xs tw-text-gray-500">Show less</span>
      </button>
      <div className="tw-px-3 tw-pb-2 tw-space-y-1">
        {tools.map((t, idx) => {
          const Icon = getSdkToolIcon(t.tool)
          const desc = getSdkToolDescription(t.tool, t.toolInput)
          return (
            <div key={idx} className="tw-flex tw-items-center tw-gap-2 tw-py-1">
              <div className={`tw-w-5 tw-h-5 tw-rounded tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${
                t.status === 'running' ? 'tw-bg-blue-100' : 'tw-bg-gray-100'
              }`}>
                {t.status === 'running' ? (
                  <BiLoaderAlt className="tw-animate-spin tw-text-blue-600" size={12} />
                ) : (
                  <Icon className="tw-text-gray-500" size={12} />
                )}
              </div>
              <span className="tw-text-xs tw-text-gray-600 tw-truncate">{desc}</span>
              {t.status === 'completed' && (
                <FiCheckCircle className="tw-text-emerald-500 tw-flex-shrink-0 tw-ml-auto" size={12} />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Process steps interface
interface DisplayStep {
  type: 'thinking' | 'tool' | 'memory' | 'sdk_tools' | 'csuite' | 'narration'
  thought?: string
  tool?: string
  toolInput?: any
  status: 'running' | 'completed' | 'failed'
  memoryUpdate?: MemoryUpdate
  sdkTools?: SdkToolInfo[] // For grouped SDK tools
  csuiteRole?: string      // C-suite executive role (CEO, CFO, etc.)
  csuiteTitle?: string     // C-suite executive title
  csuiteReview?: string    // C-suite review text
  narration?: Narration
  insight?: string
}

// Plan progress tracking
interface PlanInfo {
  items: string[]           // The plan step texts
  rawText: string           // Original plan text
}

const isPlanTool = (tool: string): boolean => {
  const name = stripMcpPrefix(tool)
  return name === 'create_analysis_plan' || name === 'create_plan'
}

const planTextFromToolOutput = (output: unknown): string => {
  if (output == null) return ''
  if (typeof output === 'string') return output
  if (typeof output === 'object' && output !== null) {
    const o = output as Record<string, unknown>
    if (typeof o.text === 'string') return o.text
    if (typeof o.content === 'string') return o.content
    if (Array.isArray(o.content)) {
      return o.content
        .filter((b: unknown) => typeof b === 'object' && b !== null && (b as { type?: string }).type === 'text')
        .map((b: unknown) => String((b as { text?: string }).text ?? ''))
        .join('\n')
    }
  }
  try {
    return JSON.stringify(output)
  } catch {
    return String(output)
  }
}

const processStepsForDisplay = (steps: AgentStep[], memoryUpdate: MemoryUpdate | null, narrations: Narration[] = []): { steps: DisplayStep[]; plan: PlanInfo | null } => {
  const result: DisplayStep[] = []
  const seenThoughts = new Set<string>()
  let hasSummary = false
  const sdkToolsList: SdkToolInfo[] = []
  let plan: PlanInfo | null = null

  for (const step of steps) {
    if (step.thought && !step.tool) {
      const thought = step.thought.trim()

      // Handle C-suite review steps embedded as agent steps
      if (thought.startsWith('csuite:')) {
        const parts = thought.split(':')
        const csuiteStatus = parts[1] // 'reviewing' or 'completed'
        const role = parts[2]
        const title = parts[3]
        const review = parts.slice(4).join(':') // review text may contain colons

        // Skip needs_more_info
        if (csuiteStatus === 'needs_more_info') continue

        // Check if we already have a csuite step for this role (update it)
        const existingIdx = result.findIndex(r => r.type === 'csuite' && r.csuiteRole === role)
        if (existingIdx >= 0) {
          result[existingIdx].status = step.status
          if (csuiteStatus === 'completed' && review) {
            result[existingIdx].csuiteReview = review
          }
          continue
        }

        result.push({
          type: 'csuite',
          status: step.status,
          csuiteRole: role,
          csuiteTitle: title,
          csuiteReview: csuiteStatus === 'completed' ? review : undefined,
        })
        continue
      }

      // Skip empty or very short generic thoughts
      if (!thought || thought.length < 10) continue
      // Skip bare "Using toolname" lines (no actual reasoning)
      if (/^Using \S+$/.test(thought)) continue

      const thoughtKey = thought.slice(0, 80).toLowerCase()
      if (seenThoughts.has(thoughtKey)) {
        continue
      }

      // Extract plan as a separate first-class element (not a regular thinking step)
      if (isPlanText(thought)) {
        const items = extractPlanItems(thought)
        if (items.length > 0) {
          plan = { items, rawText: thought }
          seenThoughts.add(thoughtKey)
          continue // Don't add to regular steps
        }
      }

      const isThisSummary = isSummaryText(thought)

      if (isThisSummary) {
        if (hasSummary) {
          const existingSummaryIdx = result.findIndex(r => r.type === 'thinking' && isSummaryText(r.thought || ''))
          if (existingSummaryIdx >= 0 && thought.length > (result[existingSummaryIdx].thought?.length || 0) * 1.5) {
            result[existingSummaryIdx].thought = thought
          }
          continue
        }
        hasSummary = true
      }

      seenThoughts.add(thoughtKey)
      result.push({
        type: 'thinking',
        thought,
        status: step.status,
      })
    }

    if (step.tool) {
      // Collect SDK tools separately for compact display
      if (isSdkTool(step.tool)) {
        sdkToolsList.push({
          tool: step.tool,
          toolInput: step.toolInput,
          status: step.status,
        })
        continue
      }

      // Skip non-MCP, non-SDK tools
      if (!isMcpTool(step.tool)) continue

      // Each MCP tool call gets its own entry in the timeline - no dedup
      // The Redux store already handles dedup via addAgentStep/updateAgentStep
      result.push({
        type: 'tool',
        tool: step.tool,
        toolInput: step.toolInput,
        status: step.status,
        insight: step.insight,
      })
    }
  }

  // Add grouped SDK tools as a single step if there are any
  if (sdkToolsList.length > 0) {
    const hasRunning = sdkToolsList.some(t => t.status === 'running')
    result.unshift({
      type: 'sdk_tools',
      status: hasRunning ? 'running' : 'completed',
      sdkTools: sdkToolsList,
    })
  }

  if (memoryUpdate) {
    result.push({
      type: 'memory',
      status: 'completed',
      memoryUpdate,
    })
  }

  if (narrations.length > 0) {
    const narrationRows = narrations.map((n) => ({
      type: 'narration' as const,
      status: 'completed' as const,
      narration: n,
    }))
    result.push(...narrationRows)
  }

  // Fall back to create_analysis_plan / create_plan tool output when no thought-based plan
  if (!plan) {
    for (const step of steps) {
      if (!step.tool || !isPlanTool(step.tool)) continue
      const raw = planTextFromToolOutput(step.toolOutput ?? step.thought)
      if (!raw) continue
      const items = extractPlanItems(raw)
      if (items.length > 0) {
        plan = { items, rawText: raw }
        break
      }
    }
  }

  return { steps: result, plan }
}

// C-Suite executive metadata for display
const csuiteMetadata: Record<string, {
  icon: React.ComponentType<any>
  label: string
  color: string
  bgColor: string
  gradientFrom: string
  gradientTo: string
}> = {
  CEO: {
    icon: FiBriefcase,
    label: 'CEO',
    color: 'tw-text-amber-700',
    bgColor: 'tw-bg-amber-50',
    gradientFrom: 'tw-from-amber-500',
    gradientTo: 'tw-to-orange-600',
  },
  CFO: {
    icon: FiDollarSign,
    label: 'CFO',
    color: 'tw-text-emerald-700',
    bgColor: 'tw-bg-emerald-50',
    gradientFrom: 'tw-from-emerald-500',
    gradientTo: 'tw-to-green-600',
  },
  CMO: {
    icon: FiTarget,
    label: 'CMO',
    color: 'tw-text-pink-700',
    bgColor: 'tw-bg-pink-50',
    gradientFrom: 'tw-from-pink-500',
    gradientTo: 'tw-to-rose-600',
  },
  CRO: {
    icon: FiTrendingUp,
    label: 'CRO',
    color: 'tw-text-blue-700',
    bgColor: 'tw-bg-blue-50',
    gradientFrom: 'tw-from-blue-500',
    gradientTo: 'tw-to-indigo-600',
  },
  COO: {
    icon: FiSettings,
    label: 'COO',
    color: 'tw-text-violet-700',
    bgColor: 'tw-bg-violet-50',
    gradientFrom: 'tw-from-violet-500',
    gradientTo: 'tw-to-purple-600',
  },
  // Deep Dive lead review — GPT-6 Astra (papaya deepDive.ts DEEP_DIVE_REVIEWER).
  ASTRA: {
    icon: FiCompass,
    label: 'Lead Analyst',
    color: 'tw-text-fuchsia-700',
    bgColor: 'tw-bg-fuchsia-50',
    gradientFrom: 'tw-from-amber-500',
    gradientTo: 'tw-to-fuchsia-600',
  },
}

// Plan progress component - always visible, tracks progress through plan steps
const PlanProgress: React.FC<{
  plan: PlanInfo
  completedToolCalls: number
  hasRunningTool: boolean
  agentStatus: 'idle' | 'running' | 'completed' | 'failed' | 'cancelled'
}> = ({ plan, completedToolCalls, hasRunningTool, agentStatus }) => {
  const totalSteps = plan.items.length
  // Map tool calls to plan steps — each tool call roughly advances one step
  // Cap at totalSteps so we don't overflow
  const completedSteps = agentStatus === 'completed' || agentStatus === 'failed' || agentStatus === 'cancelled'
    ? totalSteps
    : Math.min(completedToolCalls, totalSteps)
  const currentStep = hasRunningTool && completedSteps < totalSteps ? completedSteps : -1
  const progressPercent = Math.round((completedSteps / totalSteps) * 100)

  return (
    <div className="tw-rounded-lg tw-bg-blue-50 tw-border tw-border-blue-100 tw-px-3 tw-py-2.5">
      {/* Plan header */}
      <div className="tw-pb-2">
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
          <div className="tw-flex tw-items-center tw-gap-2">
            <BiTargetLock className="tw-text-blue-600" size={13} />
            <span className="tw-text-xs tw-font-semibold tw-text-blue-700 tw-uppercase tw-tracking-wide">Plan</span>
          </div>
          <span className="tw-text-xs tw-text-blue-700 tw-font-medium">
            {agentStatus === 'completed' || agentStatus === 'failed' || agentStatus === 'cancelled'
              ? `${totalSteps}/${totalSteps} done`
              : `${completedSteps}/${totalSteps} steps`}
          </span>
        </div>
        {/* Progress bar */}
        <div className="tw-w-full tw-h-1 tw-bg-blue-100 tw-rounded-full tw-overflow-hidden">
          <div
            className={`tw-h-full tw-rounded-full tw-transition-all tw-duration-500 tw-ease-out ${
              agentStatus === 'completed' ? 'tw-bg-emerald-500' : agentStatus === 'cancelled' ? 'tw-bg-gray-400' : 'tw-bg-blue-500'
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Plan steps */}
      <div className="tw-space-y-0.5">
        {plan.items.map((item, idx) => {
          const isCompleted = idx < completedSteps
          const isCurrent = idx === currentStep
          const isPending = !isCompleted && !isCurrent

          return (
            <div
              key={idx}
              className={`tw-flex tw-items-start tw-gap-2.5 tw-py-1 tw-transition-opacity tw-duration-300 ${
                isPending ? 'tw-opacity-50' : 'tw-opacity-100'
              }`}
            >
              {/* Status indicator */}
              <div className="tw-flex-shrink-0 tw-mt-0.5">
                {isCompleted ? (
                  <div className="tw-w-5 tw-h-5 tw-rounded-full tw-bg-emerald-100 tw-flex tw-items-center tw-justify-center">
                    <FiCheckCircle className="tw-text-emerald-600" size={12} />
                  </div>
                ) : isCurrent ? (
                  <div className="tw-w-5 tw-h-5 tw-rounded-full tw-bg-blue-100 tw-flex tw-items-center tw-justify-center">
                    <BiLoaderAlt className="tw-animate-spin tw-text-blue-600" size={12} />
                  </div>
                ) : (
                  <div className="tw-w-5 tw-h-5 tw-rounded-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center">
                    <span className="tw-text-[10px] tw-font-medium tw-text-gray-400">{idx + 1}</span>
                  </div>
                )}
              </div>

              {/* Step text — completed steps stay readable (the check icon marks
                  done); no strikethrough, which made finished plans unreadable. */}
              <span className={`tw-text-sm tw-leading-snug ${
                isCompleted ? 'tw-text-gray-600' : isCurrent ? 'tw-text-gray-800 tw-font-medium' : 'tw-text-gray-400'
              }`}>
                {item}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export const AnswerCard: React.FC<{
  text: string
  onActionClick?: (actionNumber: number, actionTitle: string) => void
  /**
   * Suppress this card's own local, unpersisted Copy/thumbs footer — for
   * callers (e.g. the no-tool-call agent reply path in AI/index.tsx) that
   * already render a richer action row underneath with backend-persisted
   * ratings, voice playback, and report export. Without this, both footers
   * would show at once with two different, conflicting thumbs states.
   */
  hideFooter?: boolean
}> = ({ text, onActionClick, hideFooter = false }) => {
  const [copied, setCopied] = useState(false)
  const [rating, setRating] = useState<'up' | 'down' | null>(null)

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // ignore clipboard failures
    }
  }

  return (
    <div className={`tw-mx-3 tw-mt-2.5 tw-border tw-border-slate-200 tw-bg-white tw-overflow-hidden tw-shadow-[0_1px_6px_rgba(15,23,42,0.06)] ${
      hideFooter ? 'tw-rounded-t-lg tw-border-b-0' : 'tw-rounded-lg tw-mb-3'
    }`}>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-bg-green-50 tw-border-b tw-border-green-200">
        <div className="tw-w-5 tw-h-5 tw-rounded-full tw-bg-green-100 tw-flex tw-items-center tw-justify-center">
          <FiCheckCircle className="tw-text-green-600" size={12} />
        </div>
        <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-green-700">Answer</span>
      </div>
      <div className="tw-px-3 tw-py-3 tw-text-gray-900 tw-text-[15px]">
        <AgentResponseRenderer content={text} bare onActionClick={onActionClick} />
      </div>
      {!hideFooter && (
        <div className="tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2 tw-border-t tw-border-gray-100">
          <button
            type="button"
            className={`tw-inline-flex tw-items-center tw-gap-1 tw-text-xs ${copied ? 'tw-text-emerald-600' : 'tw-text-gray-500 hover:tw-text-gray-700'}`}
            onClick={onCopy}
          >
            <span className={`fa ${copied ? 'fa-check' : 'fa-copy'}`} />
            <span>{copied ? 'Copied' : 'Copy'}</span>
          </button>
          <span className="tw-text-gray-300">|</span>
          <button
            type="button"
            className={`tw-inline-flex tw-items-center tw-text-xs ${rating === 'up' ? 'tw-text-blue-600' : 'tw-text-gray-500 hover:tw-text-gray-700'}`}
            onClick={() => setRating(rating === 'up' ? null : 'up')}
            title="Thumbs up"
          >
            <span className="fa fa-thumbs-up" />
          </button>
          <button
            type="button"
            className={`tw-inline-flex tw-items-center tw-text-xs ${rating === 'down' ? 'tw-text-red-600' : 'tw-text-gray-500 hover:tw-text-gray-700'}`}
            onClick={() => setRating(rating === 'down' ? null : 'down')}
            title="Thumbs down"
          >
            <span className="fa fa-thumbs-down" />
          </button>
        </div>
      )}
    </div>
  )
}

const AgentChainOfThought: React.FC<AgentChainOfThoughtProps> = ({ steps, status, finalResponse, memoryUpdate, narrations = [], messageId, creditSummary }) => {
  // Completed chats start collapsed (answer-first layout); running chats start expanded
  const isInitiallyCompleted = status !== 'running' && status !== 'idle'
  const [expanded, setExpanded] = useState(!isInitiallyCompleted)
  const timelineRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const [startTime, setStartTime] = useState<number | null>(null)
  const [elapsedTime, setElapsedTime] = useState<string>('0.0s')
  const [creditsExpanded, setCreditsExpanded] = useState(false)
  const [expandedCsuiteRoles, setExpandedCsuiteRoles] = useState<Set<string>>(new Set())

  const { steps: displaySteps, plan } = useMemo(
    () => processStepsForDisplay(steps, memoryUpdate, narrations),
    [steps, memoryUpdate, narrations],
  )
  const completedTools = steps.filter(s => s.tool && s.status === 'completed' && isMcpTool(s.tool)).length
  const runningTools = steps.filter(s => s.tool && s.status === 'running' && isMcpTool(s.tool)).length
  const totalActions = steps.length

  // Format elapsed time as a human-readable phrase, e.g. "47s" → "47 seconds", "4m 47s" → "4 minutes 47 seconds"
  const formatWorkedFor = (raw: string): string => {
    if (!raw || raw === '0.0s') return ''
    if (raw.includes('m ')) {
      const mins = parseInt(raw.split('m')[0], 10)
      const secs = parseInt(raw.split('m ')[1]?.replace('s', '') || '0', 10)
      const mLabel = mins === 1 ? '1 minute' : `${mins} minutes`
      return secs > 0 ? `${mLabel} ${secs} ${secs === 1 ? 'second' : 'seconds'}` : mLabel
    }
    const sec = Math.floor(parseFloat(raw.replace('s', '')))
    if (Number.isNaN(sec) || sec <= 0) return ''
    return sec === 1 ? '1 second' : `${sec} seconds`
  }

  // For historical chats the live timer never ran — use durationMs from the backend's
  // creditSummary (authoritative), then narration workedForMs as a legacy fallback.
  const msToElapsed = (ms: number) =>
    ms < 60000
      ? `${Math.round(ms / 1000)}s`
      : `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`
  const narrationMs = narrations.length > 0 ? Math.max(...narrations.map(n => n.workedForMs || 0)) : 0
  const fallbackMs = (creditSummary?.durationMs ?? 0) > 0
    ? creditSummary!.durationMs!
    : narrationMs
  const resolvedElapsed = (elapsedTime !== '0.0s' && elapsedTime !== '')
    ? elapsedTime
    : fallbackMs > 0
      ? msToElapsed(fallbackMs)
      : elapsedTime
  const workedForLabel = formatWorkedFor(resolvedElapsed)

  const creditGroups = useMemo(
    () => (Array.isArray(creditSummary?.groups) ? creditSummary!.groups! : []),
    [creditSummary],
  )
  const hasCredits = creditSummary != null && Number(creditSummary.total) > 0
  // Only expand when there are multiple groups — a single group is already the summary line.
  const canExpandCredits = creditGroups.length > 1

  const statsPanel = useMemo(() => {
    if (!workedForLabel && totalActions === 0 && !hasCredits) return null
    return (
      <div className="tw-px-3 tw-pt-2.5">
        <div className="tw-rounded-lg tw-overflow-hidden tw-border tw-border-gray-100 tw-text-xs">
          {workedForLabel && (
            <div className="tw-flex tw-justify-between tw-items-center tw-px-3 tw-py-2 tw-bg-gray-50 tw-border-b tw-border-gray-100">
              <span className="tw-text-gray-500">Time worked</span>
              <span className="tw-text-gray-700 tw-font-medium">{workedForLabel}</span>
            </div>
          )}
          {totalActions > 0 && (
            <div className="tw-flex tw-justify-between tw-items-center tw-px-3 tw-py-2 tw-bg-gray-50 tw-border-b tw-border-gray-100">
              <span className="tw-text-gray-500">Work done</span>
              <span className="tw-text-gray-700 tw-font-medium">{totalActions} {totalActions === 1 ? 'action' : 'actions'}</span>
            </div>
          )}
          {hasCredits && (
            <>
              <button
                type="button"
                disabled={!canExpandCredits}
                className={`tw-w-full tw-flex tw-justify-between tw-items-center tw-px-3 tw-py-2 tw-bg-gray-50 tw-text-left${canExpandCredits ? ' hover:tw-bg-gray-100' : ''}`}
                onClick={canExpandCredits ? () => setCreditsExpanded(v => !v) : undefined}
              >
                <span className="tw-text-gray-500">Credits used</span>
                <div className="tw-flex tw-items-center tw-gap-1">
                  <span className="tw-text-gray-700 tw-font-medium">{formatCredits(Number(creditSummary!.total))} credits</span>
                  {canExpandCredits && (
                    creditsExpanded
                      ? <BiChevronDown size={13} className="tw-text-gray-400" />
                      : <BiChevronRight size={13} className="tw-text-gray-400" />
                  )}
                </div>
              </button>
              {creditsExpanded && creditGroups.map((g) => (
                <div key={g.label} className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-1.5 tw-bg-white tw-border-t tw-border-gray-100">
                  <span className="tw-text-gray-400">{g.label} × {g.count}</span>
                  <span className="tw-text-gray-500 tw-font-medium">{formatCredits(g.credits)}</span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    )
  }, [workedForLabel, totalActions, hasCredits, canExpandCredits, creditsExpanded, creditSummary, creditGroups])

  // Effective status: show "running" if there are still-running steps, even if
  // the backend sent agent_status: 'completed' prematurely (e.g. C-suite re-run, error recovery).
  // This keeps the progress indicator visible as long as work is visually happening.
  const effectiveStatus =
    status === 'cancelled'
      ? 'cancelled'
      : (status === 'running' || runningTools > 0)
        ? 'running'
        : status

  useEffect(() => {
    if (effectiveStatus === 'running' && startTime === null) {
      setStartTime(Date.now())
    }
  }, [effectiveStatus, startTime])

  // When a live agent run finishes, collapse the CoT so the answer-first layout kicks in
  useEffect(() => {
    if (effectiveStatus !== 'running' && finalResponse) {
      setExpanded(false)
    }
  }, [effectiveStatus, finalResponse])

  // Answer-first: show the AnswerCard above the CoT card for completed chats
  const showAnswerFirst = !!finalResponse && effectiveStatus !== 'running'

  // TEMP DIAGNOSTIC — remove once the no-spinner bug is confirmed fixed.
  console.log('[DIAG-STREAM] AgentChainOfThought render', {
    status,
    runningTools,
    effectiveStatus,
    showAnswerFirst,
    finalResponsePreview: finalResponse ? String(finalResponse).slice(0, 60) : finalResponse,
    stepsCount: steps.length,
    lastStepStatus: steps[steps.length - 1]?.status,
    lastStepTool: steps[steps.length - 1]?.tool,
  })
  const navigate = useNavigate()

  useEffect(() => {
    if (effectiveStatus !== 'running' || startTime === null) return

    const updateTimer = () => {
      const elapsed = (Date.now() - startTime) / 1000
      if (elapsed < 60) {
        setElapsedTime(`${elapsed.toFixed(1)}s`)
      } else {
        const mins = Math.floor(elapsed / 60)
        const secs = Math.floor(elapsed % 60)
        setElapsedTime(`${mins}m ${secs}s`)
      }
    }

    updateTimer()
    const interval = setInterval(updateTimer, 1000)

    return () => clearInterval(interval)
  }, [effectiveStatus, startTime])

  useEffect(() => {
    if (bottomRef.current && effectiveStatus === 'running') {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
      }, 50)
    }
  }, [displaySteps.length, effectiveStatus])

  if (displaySteps.length === 0 && !plan && status === 'idle') {
    return null
  }

  // Initial analyzing state — only show if we don't have a plan yet
  if (displaySteps.length === 0 && !plan && effectiveStatus === 'running') {
    return (
      <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-mt-4 tw-overflow-hidden">
        <div className="tw-flex tw-items-center tw-gap-4 tw-p-4">
          <div className="tw-w-10 tw-h-10 tw-rounded-xl tw-bg-gradient-to-br tw-from-indigo-500 tw-to-purple-600 tw-flex tw-items-center tw-justify-center tw-shadow-sm tw-flex-shrink-0">
            <BiLoaderAlt className="tw-animate-spin tw-text-white" size={20} />
          </div>
          <div className="tw-flex-1">
            <div className="tw-text-sm tw-font-semibold tw-text-gray-800">Thinking…</div>
            <div className="tw-text-xs tw-text-gray-500 tw-mt-0.5">Preparing a response</div>
          </div>
          <div className="tw-text-sm tw-font-mono tw-text-gray-500 tw-tabular-nums">
            {elapsedTime}
          </div>
        </div>
      </div>
    )
  }

  // Header + collapsible body (parity with mobile CoT layout)
  return (
    <div className="tw-mt-3 tw-mb-3">
      {/* Answer-first: show the response above the tool timeline for completed chats */}
      {showAnswerFirst && (
        <AnswerCard text={stripLeadingPlanBlock(finalResponse!)} />
      )}
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
      {/* Header strip */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={`tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-left hover:tw-bg-gray-50 tw-transition-colors ${expanded ? 'tw-border-b tw-border-gray-100' : ''}`}
      >
        <BiStopwatch
          className={effectiveStatus === 'running' ? 'tw-text-blue-400 tw-flex-shrink-0' : 'tw-text-gray-400 tw-flex-shrink-0'}
          size={15}
        />
        <span className="tw-text-xs tw-text-gray-600 tw-font-medium tw-flex-1">
          {effectiveStatus === 'running'
            ? `Working for ${elapsedTime}`
            : workedForLabel
              ? `Worked for ${workedForLabel}`
              : 'Completed'}
        </span>
        {showAnswerFirst && messageId && (
          <span
            role="link"
            tabIndex={0}
            className="tw-text-[10px] tw-text-indigo-500 hover:tw-text-indigo-700 tw-whitespace-nowrap tw-flex-shrink-0 tw-mr-1 tw-cursor-pointer"
            onClick={(e) => {
              e.stopPropagation()
              navigate(`/ai?view=tool-access&chatId=${messageId}`)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation()
                navigate(`/ai?view=tool-access&chatId=${messageId}`)
              }
            }}
            title="View tool calls in Tool Access"
          >
            Tool Access →
          </span>
        )}
        {expanded ? (
          <BiChevronDown size={16} className="tw-text-gray-400 tw-flex-shrink-0" />
        ) : (
          <BiChevronRight size={16} className="tw-text-gray-400 tw-flex-shrink-0" />
        )}
      </button>

      {expanded && (
        <>
          {/* Stats summary — Replit-style breakdown for completed runs */}
          {effectiveStatus !== 'running' && statsPanel}

          {/* Plan progress — always visible at top when plan exists */}
          {plan && (
            <div className="tw-px-3 tw-pt-2.5">
              <PlanProgress
                plan={plan}
                completedToolCalls={completedTools}
                hasRunningTool={runningTools > 0}
                agentStatus={status}
              />
            </div>
          )}

          {/* Action timeline card */}
          <div className="tw-px-3 tw-pt-2.5">
            <div ref={timelineRef} className="tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-overflow-hidden">
        {displaySteps.map((step, idx) => {
          if (step.type === 'thinking') {
            return <ThinkingRow key={`think-${idx}`} thought={step.thought || ''} />
          }

          if (step.type === 'tool') {
            const meta = getToolMeta(step.tool || '')
            const Icon = meta.icon
            const query = formatToolQuery(step.toolInput)
            const params = extractToolParams(step.toolInput)
            const dataSource = detectDataSource(query || '', step.tool || '')

            return (
              <div key={`tool-${step.tool}-${idx}`} className="tw-flex tw-items-start tw-gap-3 tw-px-3 tw-py-2.5" title={meta.tooltip}>
                <div className={`tw-w-7 tw-h-7 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-mt-0.5 ${
                  step.status === 'running' ? 'tw-bg-blue-100' : step.status === 'failed' ? 'tw-bg-red-50' : (meta.bgColor || 'tw-bg-gray-100')
                }`}>
                  {step.status === 'running' ? (
                    <BiLoaderAlt className="tw-animate-spin tw-text-blue-600" size={15} />
                  ) : step.status === 'failed' ? (
                    <FiXCircle className="tw-text-red-500" size={15} />
                  ) : (
                    <Icon className={meta.color || 'tw-text-gray-600'} size={15} />
                  )}
                </div>
                <div className="tw-flex-1 tw-min-w-0">
                  {/* Tool label + status */}
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <span className={`tw-text-xs tw-font-semibold ${meta.color || 'tw-text-gray-500'}`}>{meta.label}</span>
                    {dataSource && (
                      <span className="tw-text-[10px] tw-text-gray-400 tw-flex tw-items-center tw-gap-1">
                        <dataSource.icon size={10} className={dataSource.color} />
                        {dataSource.label}
                      </span>
                    )}
                    <div className="tw-ml-auto tw-flex-shrink-0">
                      {step.status === 'completed' && (
                        <FiCheckCircle className="tw-text-emerald-500" size={13} />
                      )}
                      {step.status === 'failed' && (
                        <span className="tw-text-[10px] tw-text-red-400 tw-font-medium">failed</span>
                      )}
                    </div>
                  </div>
                  {/* Query description - allow wrapping */}
                  {query && (
                    <p className={`tw-text-sm tw-leading-snug tw-mt-0.5 tw-mb-0 tw-line-clamp-2 ${
                      step.status === 'failed' ? 'tw-text-red-600' : 'tw-text-gray-700'
                    }`}>{query}</p>
                  )}
                  {/* Parameters as inline tags */}
                  {params.length > 0 && (
                    <div className="tw-flex tw-flex-wrap tw-gap-x-3 tw-gap-y-0.5 tw-mt-1">
                      {params.slice(0, 4).map((p, pIdx) => (
                        <span key={pIdx} className="tw-text-[11px] tw-text-gray-400 tw-flex tw-items-center tw-gap-1">
                          {p.icon && <p.icon size={10} className="tw-text-gray-400" />}
                          <span className="tw-text-gray-500">{p.key}:</span> {p.value}
                        </span>
                      ))}
                    </div>
                  )}
                  {step.insight && (
                    <div className="tw-mt-1 tw-text-xs tw-text-indigo-600 tw-italic">{step.insight}</div>
                  )}
                </div>
              </div>
            )
          }

          // SDK tools - compact tiled display like Replit
          if (step.type === 'sdk_tools' && step.sdkTools) {
            const sdkTools = step.sdkTools
            const completedCount = sdkTools.filter(t => t.status === 'completed').length
            const isRunning = sdkTools.some(t => t.status === 'running')

            return (
              <SdkToolsRow
                key="sdk-tools"
                tools={sdkTools}
                completedCount={completedCount}
                isRunning={isRunning}
              />
            )
          }

          if (step.type === 'memory') {
            return (
              <div key="memory" className="tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2.5">
                <div className="tw-w-6 tw-h-6 tw-rounded tw-bg-sky-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                  <BiMemoryCard className="tw-text-sky-600" size={14} />
                </div>
                <span className="tw-text-sm tw-text-gray-700">
                  Context optimized — saved {Math.round((step.memoryUpdate?.savedChars || 0) / 1000)}KB
                </span>
              </div>
            )
          }

          if (step.type === 'narration' && step.narration?.text) {
            return (
              <div key={`narration-${idx}`} className="tw-flex tw-items-start tw-gap-3 tw-px-3 tw-py-2.5">
                <div className="tw-w-6 tw-h-6 tw-rounded tw-bg-violet-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                  <BiBrain className="tw-text-violet-600" size={13} />
                </div>
                <div className="tw-text-sm tw-text-gray-700 tw-leading-snug">{step.narration.text}</div>
              </div>
            )
          }

          if (step.type === 'csuite' && step.csuiteRole) {
            const meta = csuiteMetadata[step.csuiteRole] || csuiteMetadata.CEO
            const Icon = meta.icon
            // Hide csuite steps that have no useful content:
            // - Stuck (still running when agent is done) with no review
            // - Completed but with no review text (empty/pointless)
            const isStuck = step.status === 'running' && effectiveStatus === 'completed'
            if (isStuck && !step.csuiteReview) return null
            if (step.status === 'completed' && !step.csuiteReview) return null // Hide empty completed reviews

            const isReviewing = step.status === 'running'
            const isCompleted = step.status === 'completed'
            const hasReview = isCompleted && !!step.csuiteReview
            const isExpanded = expandedCsuiteRoles.has(step.csuiteRole)

            const toggleExpand = () => {
              if (!hasReview) return
              setExpandedCsuiteRoles(prev => {
                const next = new Set(prev)
                if (next.has(step.csuiteRole!)) {
                  next.delete(step.csuiteRole!)
                } else {
                  next.add(step.csuiteRole!)
                }
                return next
              })
            }

            return (
              <div
                key={`csuite-${step.csuiteRole}`}
                className={`tw-flex tw-items-start tw-gap-3 tw-px-3 tw-py-2.5 ${hasReview ? 'tw-cursor-pointer hover:tw-bg-gray-50 tw-transition-colors' : ''}`}
                onClick={toggleExpand}
              >
                <div className={`tw-w-7 tw-h-7 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-mt-0.5 ${
                  isReviewing ? 'tw-bg-blue-100' : (meta.bgColor || 'tw-bg-gray-100')
                }`}>
                  {isReviewing ? (
                    <BiLoaderAlt className="tw-animate-spin tw-text-blue-600" size={15} />
                  ) : (
                    <Icon className={meta.color || 'tw-text-gray-600'} size={15} />
                  )}
                </div>
                <div className="tw-flex-1 tw-min-w-0">
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <span className={`tw-text-xs tw-font-semibold ${meta.color || 'tw-text-gray-500'}`}>
                      {step.csuiteTitle}
                    </span>
                    {isReviewing && (
                      <span className="tw-text-[10px] tw-text-blue-500 tw-font-medium">Reviewing...</span>
                    )}
                    <div className="tw-ml-auto tw-flex-shrink-0 tw-flex tw-items-center tw-gap-1">
                      {isCompleted && (
                        <FiCheckCircle className="tw-text-emerald-500" size={13} />
                      )}
                      {hasReview && (
                        isExpanded
                          ? <BiChevronDown className="tw-text-gray-400" size={16} />
                          : <BiChevronRight className="tw-text-gray-400" size={16} />
                      )}
                    </div>
                  </div>
                  {hasReview && !isExpanded && (
                    <p className="tw-text-sm tw-leading-snug tw-mt-0.5 tw-mb-0 tw-line-clamp-2 tw-text-gray-700">
                      {step.csuiteReview}
                    </p>
                  )}
                  {hasReview && isExpanded && (
                    <p className="tw-text-sm tw-leading-snug tw-mt-0.5 tw-mb-0 tw-text-gray-700 tw-whitespace-pre-wrap">
                      {step.csuiteReview}
                    </p>
                  )}
                </div>
              </div>
            )
          }

          return null
        })}

        {/* Running indicator */}
        {effectiveStatus === 'running' && runningTools === 0 && !displaySteps.some(s => s.type === 'csuite' && s.status === 'running') && (
          <div className="tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2.5">
            <div className="tw-w-6 tw-h-6 tw-rounded tw-bg-indigo-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
              <BiLoaderAlt className="tw-animate-spin tw-text-indigo-600" size={14} />
            </div>
            <span className="tw-text-sm tw-text-gray-700">Preparing response...</span>
          </div>
        )}

        {effectiveStatus === 'cancelled' && (
          <div className="tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2.5 tw-bg-gray-50">
            <div className="tw-w-6 tw-h-6 tw-rounded tw-bg-gray-200 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
              <FiXCircle className="tw-text-gray-600" size={14} />
            </div>
            <span className="tw-text-sm tw-text-gray-600">Response stopped</span>
          </div>
        )}
            </div>
          </div>

          {/* Timer footer when running */}
          {effectiveStatus === 'running' && (
            <div className="tw-px-3 tw-py-2 tw-bg-gray-50">
              <span className="tw-text-xs tw-font-mono tw-text-gray-400 tw-tabular-nums">{elapsedTime}</span>
            </div>
          )}
        </>
      )}

      {/* Scroll anchor */}
      <div ref={bottomRef} />
    </div>
    </div>
  )
}

export default AgentChainOfThought
